import { httpClient } from '@/shared/api';
import { CALC_BAND_NEW_ID } from './schoolBands.dto';
import { PARENT_BAND_EMPTY_ID, type ParentBandEditableRow } from './parentTuitionBand.dto';
import type { NestedTuitionBandDto, SchoolBandWithTuitionBandsDto } from './tuitionBand.dto';
import type { SchoolClassificationDashboardView } from './schoolClassificationDashboard.dto';
import type { ClassTuitionByProgramRow } from './classTuitionByProgram.dto';
import type {
  PeerMeasureTriplet,
  SchoolClassificationSchoolComparisonView,
  SchoolComparisonPeerGroupView,
  SchoolComparisonTuitionBandRangeView,
} from './schoolClassificationComparison.dto';
import {
  labelBudgetSubmittedStatus,
  labelReviewStatus,
  parseBudgetSubmittedStatus,
  parseReviewStatus,
} from './schoolClassificationDashboard.enums';

const PREFIX = '/app/school-classification';

/**
 * Accept any 8-4-4-4-12 hex id (ABP / SQL Server GUIDs are not always RFC-4122 variant bits).
 * Strict RFC-only regex was rejecting some server tuition/parent ids while school-band ids matched.
 */
const LOOSE_GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isPersistedEntityGuid(id: string): boolean {
  const t = id.trim().toLowerCase();
  if (!LOOSE_GUID_RE.test(t)) return false;
  if (t === CALC_BAND_NEW_ID.toLowerCase()) return false;
  if (t === PARENT_BAND_EMPTY_ID.toLowerCase()) return false;
  return true;
}

/** First non-empty candidate that looks like a persisted server entity id (ABP GUID). */
function pickPersistedEntityId(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  for (const c of candidates) {
    const t = c?.trim();
    if (t && isPersistedEntityGuid(t)) return t;
  }
  return undefined;
}

/** Nested tuition rows from calc/rebuild often omit `year`; mapper defaults to `0`. */
function effectiveAcademicYear(value: number | undefined | null, fallback: number): number {
  const n = value == null ? NaN : Number(value);
  if (Number.isFinite(n) && n > 0) return n;
  return fallback;
}

/**
 * Nested tuition DTOs may carry the parent school-band id in `id` while the real tuition entity id
 * lives on other keys (same ordering as `tuitionBand.api` nested mapping). Never return the size-band id here.
 */
function pickNestedTuitionBandIdForClassification(
  tb: NestedTuitionBandDto,
  parentSchoolBandId: string
): string | undefined {
  const o = tb as NestedTuitionBandDto & Record<string, unknown>;
  const p = parentSchoolBandId.trim().toLowerCase();
  const seq: Array<unknown> = [
    o.tuitionBandId,
    o.TuitionBandId,
    o.bandId,
    o.BandId,
    o.entityId,
    o.EntityId,
    o.id,
    o.Id,
    tb.id,
  ];
  for (const raw of seq) {
    if (raw == null || raw === '') continue;
    const id = pickPersistedEntityId(String(raw).trim());
    if (!id) continue;
    if (id.toLowerCase() === p) continue;
    return id;
  }
  return undefined;
}

/**
 * Matches C# `default(Guid)` — server expects `Id` on every band; use this when no DB id exists.
 * Same value as {@link CALC_BAND_NEW_ID}.
 */
const EMPTY_ENTITY_ID = CALC_BAND_NEW_ID;

/** POST body for create-school-classification-based-on-bands (camelCase JSON). */
export interface CreateSchoolClassificationTuitionBandBody {
  /** Real DB id, or {@link EMPTY_ENTITY_ID} when none. */
  id: string;
  name: string;
  minValue: number;
  maxValue: number;
  year: number;
  /** Parent size-band id, or {@link EMPTY_ENTITY_ID} when the size band is not persisted yet. */
  schoolBandId: string;
  description?: string;
}

export interface CreateSchoolClassificationSchoolBandBody {
  /** Real DB id, or {@link EMPTY_ENTITY_ID} when none. */
  id: string;
  name: string;
  minValue: number;
  maxValue: number;
  description?: string;
  minSchoolsPerBand: number;
  year: number;
  tuitionBands: CreateSchoolClassificationTuitionBandBody[];
}

export interface CreateSchoolClassificationParentBandBody {
  /** Real DB id, or {@link EMPTY_ENTITY_ID} when none. */
  id: string;
  name: string;
  minValue: number;
  maxValue: number;
  minSchoolsPerBand: number;
  year: number;
  description?: string;
}

export interface CreateSchoolClassificationBasedOnBandsBody {
  schoolBands: CreateSchoolClassificationSchoolBandBody[];
  parentBands: CreateSchoolClassificationParentBandBody[];
}

/**
 * Maps wizard calc output + parent-market rows to the create-school-classification payload.
 * Every `EditSchoolBandDto` / nested tuition / `EditParentTuitionBandDto` gets an `id`: real GUID
 * from the client state when present, otherwise `00000000-0000-0000-0000-000000000000` (C# default Guid).
 */
export function buildCreateSchoolClassificationBasedOnBandsBody(
  schoolBands: SchoolBandWithTuitionBandsDto[],
  parentRows: ParentBandEditableRow[],
  fallbackMinSchoolsPerBand: number,
  fallbackYear: number
): CreateSchoolClassificationBasedOnBandsBody {
  const mappedSchoolBands: CreateSchoolClassificationSchoolBandBody[] = schoolBands.map(
    (band) => {
      const rawId = pickPersistedEntityId(band.id);
      const schoolBandEntityId = rawId ?? EMPTY_ENTITY_ID;
      const year = effectiveAcademicYear(band.year, fallbackYear);
      const minSchools = Number.isFinite(band.minSchoolsPerBand)
        ? band.minSchoolsPerBand
        : fallbackMinSchoolsPerBand;
      const desc = (band.description ?? '').trim();
      const tuitionBands: CreateSchoolClassificationTuitionBandBody[] = (
        band.tuitionBands ?? []
      ).map((tb) => {
        const tbYear = effectiveAcademicYear(tb.year, year);
        const tbDesc = (tb.description ?? '').trim();
        const row: CreateSchoolClassificationTuitionBandBody = {
          id:
            pickNestedTuitionBandIdForClassification(tb, schoolBandEntityId) ??
            EMPTY_ENTITY_ID,
          name: (tb.name ?? '').trim() || 'Tuition band',
          minValue: tb.minValue,
          maxValue: tb.maxValue,
          year: tbYear,
          schoolBandId: schoolBandEntityId,
        };
        if (tbDesc) row.description = tbDesc;
        return row;
      });
      const row: CreateSchoolClassificationSchoolBandBody = {
        id: schoolBandEntityId,
        name: (band.name ?? '').trim() || 'School band',
        minValue: band.minValue,
        maxValue: band.maxValue,
        minSchoolsPerBand: minSchools,
        year,
        tuitionBands,
      };
      if (desc) row.description = desc;
      return row;
    }
  );

  const parentBands: CreateSchoolClassificationParentBandBody[] = parentRows.map((r) => {
    const y = effectiveAcademicYear(r.year, fallbackYear);
    const minSchools = Number.isFinite(r.minSchoolsPerBand ?? NaN)
      ? (r.minSchoolsPerBand as number)
      : fallbackMinSchoolsPerBand;
    const pdesc = (r.description ?? '').trim();
    const row: CreateSchoolClassificationParentBandBody = {
      id: pickPersistedEntityId(r.serverId, r.key) ?? EMPTY_ENTITY_ID,
      name: r.name.trim() || 'Parent-facing tuition band',
      minValue: r.minValue,
      maxValue: r.maxValue,
      minSchoolsPerBand: minSchools,
      year: y,
    };
    if (pdesc) row.description = pdesc;
    return row;
  });

  return { schoolBands: mappedSchoolBands, parentBands };
}

function readDashboardString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return undefined;
}

function readDashboardNumber(o: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readDashboardBool(o: Record<string, unknown>, keys: string[]): boolean | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'boolean') return v;
    if (v === 1 || v === '1' || v === 'true' || v === 'True') return true;
    if (v === 0 || v === '0' || v === 'false' || v === 'False') return false;
  }
  return undefined;
}

/** Fraction in [0, 1]; values in (1, 100] treated as whole percent. */
function readFraction01(o: Record<string, unknown>, keys: string[]): number | undefined {
  const n = readDashboardNumber(o, keys);
  if (n === null || !Number.isFinite(n)) return undefined;
  if (n >= 0 && n <= 1) return n;
  if (n > 1 && n <= 100) return n / 100;
  return undefined;
}

function readDashboardRaw(o: Record<string, unknown>, keys: string[]): unknown {
  for (const k of keys) {
    const v = o[k];
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    return v;
  }
  return undefined;
}

/** Many dashboard payloads nest band display names under objects (`schoolBand.name`) instead of flat `schoolBandName`. */
function readNestedBandName(o: Record<string, unknown>, objectKeys: string[]): string | undefined {
  for (const k of objectKeys) {
    const node = o[k];
    if (!node || typeof node !== 'object') continue;
    const rec = node as Record<string, unknown>;
    const name = rec.name ?? rec.Name;
    if (typeof name === 'string' && name.trim()) return name.trim();
  }
  return undefined;
}

/** Maps GET school-classificationfor-dashboard response (camelCase / PascalCase). */
export function mapSchoolClassificationDashboard(raw: unknown): SchoolClassificationDashboardView {
  if (!raw || typeof raw !== 'object') {
    return { dataCompleteness: null };
  }
  const o = raw as Record<string, unknown>;
  const budgetKeys = [
    'budgetSubmitedStatus',
    'BudgetSubmitedStatus',
    'budgetSubmittedStatus',
    'BudgetSubmittedStatus',
  ] as const;
  const budgetRaw = readDashboardRaw(o, [...budgetKeys]);
  const budgetKind = parseBudgetSubmittedStatus(budgetRaw);
  const budgetLabel =
    budgetKind !== null ? labelBudgetSubmittedStatus(budgetKind) : readDashboardString(o, [...budgetKeys]);

  const reviewKeys = ['reviewStatus', 'ReviewStatus'] as const;
  const reviewRaw = readDashboardRaw(o, [...reviewKeys]);
  const reviewKind = parseReviewStatus(reviewRaw);
  const reviewLabel =
    reviewKind !== null ? labelReviewStatus(reviewKind) : readDashboardString(o, [...reviewKeys]);

  return {
    budgetSubmittedStatus: budgetLabel,
    budgetSubmittedStatusKind: budgetKind,
    dataCompleteness: readDashboardNumber(o, ['dataCompleteness', 'DataCompleteness']),
    reviewStatus: reviewLabel,
    reviewStatusKind: reviewKind,
    publicationRole: readDashboardString(o, ['publicationRole', 'PublicationRole']),
    schoolBandName:
      readDashboardString(o, ['schoolBandName', 'SchoolBandName']) ??
      readNestedBandName(o, ['schoolBand', 'SchoolBand']),
    tuitionBandName:
      readDashboardString(o, ['tuitionBandName', 'TuitionBandName']) ??
      readNestedBandName(o, ['tuitionBand', 'TuitionBand']),
    parentBandName:
      readDashboardString(o, ['parentBandName', 'ParentBandName']) ??
      readNestedBandName(o, ['parentTuitionBand', 'ParentTuitionBand', 'parentBand', 'ParentBand']),
    schoolName: readDashboardString(o, ['schoolName', 'SchoolName']),
    educationalCenterNumber: readDashboardString(o, [
      'educationalCenterNumber',
      'EducationalCenterNumber',
    ]),
    totalStudentCount: (() => {
      const n = readDashboardNumber(o, ['totalStudentCount', 'TotalStudentCount']);
      return n === null ? undefined : n;
    })(),
    teacherCount: (() => {
      const n = readDashboardNumber(o, ['teacherCount', 'TeacherCount']);
      return n === null ? undefined : n;
    })(),
    permanentTeacherCount: readDashboardNumberOrUndefined(o, [
      'perTeacherCount',
      'PerTeacherCount',
      'permanentTeacherCount',
      'PermanentTeacherCount',
    ]),
    contractualTeacherCount: readDashboardNumberOrUndefined(o, [
      'contTeacherCount',
      'ContTeacherCount',
      'contractualTeacherCount',
      'ContractualTeacherCount',
    ]),
    adminTeacherCount: readDashboardNumberOrUndefined(o, [
      'adminTeacherCount',
      'AdminTeacherCount',
      'administrativeTeacherCount',
      'AdministrativeTeacherCount',
    ]),
    studentTeacherRatio: (() => {
      const n = readDashboardNumber(o, ['studentTeacherRatio', 'StudentTeacherRatio']);
      return n === null ? undefined : n;
    })(),
    studentStaffRatio: (() => {
      const n = readDashboardNumber(o, ['studentStaffRatio', 'StudentStaffRatio']);
      return n === null ? undefined : n;
    })(),
    averageTeacherSalaryUSD: (() => {
      const n = readDashboardNumber(o, ['averageTeacherSalaryUSD', 'AverageTeacherSalaryUSD']);
      return n === null ? undefined : n;
    })(),
    averagePermanentTeacherSalaryUSD: readDashboardNumberOrUndefined(o, [
      'averagePerTeacherSalaryUSD',
      'AveragePerTeacherSalaryUSD',
      'averagePermanentTeacherSalaryUSD',
      'AveragePermanentTeacherSalaryUSD',
    ]),
    averageContractualTeacherSalaryUSD: readDashboardNumberOrUndefined(o, [
      'averageContTeacherSalaryUSD',
      'AverageContTeacherSalaryUSD',
      'averageContractualTeacherSalaryUSD',
      'AverageContractualTeacherSalaryUSD',
    ]),
    averageAdminTeacherSalaryUSD: readDashboardNumberOrUndefined(o, [
      'averageAdminTeacherSalaryUSD',
      'AverageAdminTeacherSalaryUSD',
      'averageAdministrativeTeacherSalaryUSD',
      'AverageAdministrativeTeacherSalaryUSD',
    ]),
    tuitionUSD: (() => {
      const n = readDashboardNumber(o, ['tuitionUSD', 'TuitionUSD']);
      return n === null ? undefined : n;
    })(),
    parentTuitionUSD: (() => {
      const n = readDashboardNumber(o, ['parentTuitionUSD', 'ParentTuitionUSD']);
      return n === null ? undefined : n;
    })(),
    budgetSplit_AbPercent: readFraction01(o, [
      'budgetSplit_AbPercent',
      'BudgetSplit_AbPercent',
      'budgetSplitAbPercent',
      'BudgetSplitAbPercent',
    ]),
    budgetSplit_CPercent: readFraction01(o, [
      'budgetSplit_CPercent',
      'BudgetSplit_CPercent',
      'budgetSplitCPercent',
      'BudgetSplitCPercent',
    ]),
    budgetSplit_AbTotalUSD: (() => {
      const n = readDashboardNumber(o, [
        'budgetSplit_AbTotalUSD',
        'BudgetSplit_AbTotalUSD',
        'budgetSplitAbTotalUSD',
        'BudgetSplitAbTotalUSD',
      ]);
      return n === null ? undefined : n;
    })(),
    budgetSplit_CTotalUSD: (() => {
      const n = readDashboardNumber(o, [
        'budgetSplit_CTotalUSD',
        'BudgetSplit_CTotalUSD',
        'budgetSplitCTotalUSD',
        'BudgetSplitCTotalUSD',
      ]);
      return n === null ? undefined : n;
    })(),
    budgetSplit_Compliant: readDashboardBool(o, [
      'budgetSplit_Compliant',
      'BudgetSplit_Compliant',
      'budgetSplitCompliant',
      'BudgetSplitCompliant',
    ]),
    totalRevenueUSD: readDashboardNumberOrUndefined(o, [
      'totalRevenueUSD',
      'TotalRevenueUSD',
      'budgetSummary_TotalRevenuesTotaltUSD',
      'BudgetSummary_TotalRevenuesTotaltUSD',
      'budgetSummary_TotalRevenuesTotalUSD',
      'BudgetSummary_TotalRevenuesTotalUSD',
    ]),
    totalExpensesUSD: readDashboardNumberOrUndefined(o, [
      'totalExpensesUSD',
      'TotalExpensesUSD',
      'budgetSummary_TotalExpensesTotaltUSD',
      'BudgetSummary_TotalExpensesTotaltUSD',
      'budgetSummary_TotalExpensesTotalUSD',
      'BudgetSummary_TotalExpensesTotalUSD',
    ]),
    tuitionPerStudentUSD: readTuitionPerStudentUSD(o),
    costPerStudentUSD: readDashboardNumberOrUndefined(o, [
      'costPerStudentUSD',
      'CostPerStudentUSD',
      'budgetSummary_CostPerStudentTotaltUSD',
      'BudgetSummary_CostPerStudentTotaltUSD',
    ]),
    surplusUSD: readDashboardNumberOrUndefined(o, ['surplusUSD', 'SurplusUSD']),
    budgetSummaryATotalMainCurrency: readDashboardNumberOrUndefined(o, [
      'BudgetSummary_ATotalMainCurruncy',
      'budgetSummary_ATotalMainCurruncy',
      'BudgetSummaryATotalMainCurruncy',
      'budgetSummaryATotalMainCurruncy',
      'budgetSummary_ATotalMainCurrency',
      'BudgetSummary_ATotalMainCurrency',
    ]),
    budgetSummaryBTotalMainCurrency: readDashboardNumberOrUndefined(o, [
      'BudgetSummary_BTotalMainCurruncy',
      'budgetSummary_BTotalMainCurruncy',
      'BudgetSummaryBTotalMainCurruncy',
      'budgetSummaryBTotalMainCurruncy',
      'budgetSummary_BTotalMainCurrency',
      'BudgetSummary_BTotalMainCurrency',
    ]),
    budgetSummaryCTotalMainCurrency: readDashboardNumberOrUndefined(o, [
      'BudgetSummary_CTotalMainCurruncy',
      'budgetSummary_CTotalMainCurruncy',
      'BudgetSummaryCTotalMainCurruncy',
      'budgetSummaryCTotalMainCurruncy',
      'budgetSummary_CTotalMainCurrency',
      'BudgetSummary_CTotalMainCurrency',
    ]),
    budgetSummaryDTotalMainCurrency: readDashboardNumberOrUndefined(o, [
      'BudgetSummary_DTotalMainCurruncy',
      'budgetSummary_DTotalMainCurruncy',
      'BudgetSummaryDTotalMainCurruncy',
      'budgetSummaryDTotalMainCurruncy',
      'budgetSummary_DTotalMainCurrency',
      'BudgetSummary_DTotalMainCurrency',
    ]),
  };
}

function readDashboardNumberOrUndefined(
  o: Record<string, unknown>,
  keys: string[]
): number | undefined {
  const n = readDashboardNumber(o, keys);
  return n === null ? undefined : n;
}

/** Mirrors server: average school tuition, else parent average. */
function readTuitionPerStudentUSD(o: Record<string, unknown>): number | undefined {
  const direct = readDashboardNumber(o, [
    'tuitionPerStudentUSD',
    'TuitionPerStudentUSD',
    'revenue_AverageTuitionUSD',
    'Revenue_AverageTuitionUSD',
  ]);
  if (direct !== null && direct > 0) return direct;
  const parent = readDashboardNumber(o, [
    'revenue_Parent_AverageTuitionUSD',
    'Revenue_Parent_AverageTuitionUSD',
  ]);
  if (parent !== null && parent > 0) return parent;
  if (direct !== null) return direct;
  return parent === null ? undefined : parent;
}

function readTripletFromObject(node: unknown): PeerMeasureTriplet | null {
  if (!node || typeof node !== 'object') return null;
  const r = node as Record<string, unknown>;
  const realValue = readDashboardNumber(r, ['realValue', 'RealValue']);
  const median = readDashboardNumber(r, ['median', 'Median']);
  const average = readDashboardNumber(r, ['average', 'Average']);
  if (realValue === null && median === null && average === null) return null;
  const tri: PeerMeasureTriplet = {};
  if (realValue !== null && Number.isFinite(realValue)) tri.realValue = realValue;
  if (median !== null && Number.isFinite(median)) tri.median = median;
  if (average !== null && Number.isFinite(average)) tri.average = average;
  return Object.keys(tri).length ? tri : null;
}

/** Nested metric object or `PrefixRealValue` / `PrefixMedian` / `PrefixAverage` on peer DTO. */
function readMeasureTriplet(
  o: Record<string, unknown>,
  nestedKeys: string[],
  flatPrefix: string
): PeerMeasureTriplet | null {
  for (const k of nestedKeys) {
    const node = o[k];
    const tri = readTripletFromObject(node);
    if (tri) return tri;
  }
  const cap = flatPrefix.charAt(0).toUpperCase() + flatPrefix.slice(1);
  const realValue = readDashboardNumber(o, [
    `${flatPrefix}RealValue`,
    `${cap}RealValue`,
    `${flatPrefix}Real`,
    `${cap}Real`,
  ]);
  const median = readDashboardNumber(o, [`${flatPrefix}Median`, `${cap}Median`]);
  const average = readDashboardNumber(o, [`${flatPrefix}Average`, `${cap}Average`]);
  if (realValue === null && median === null && average === null) return null;
  const tri: PeerMeasureTriplet = {};
  if (realValue !== null && Number.isFinite(realValue)) tri.realValue = realValue;
  if (median !== null && Number.isFinite(median)) tri.median = median;
  if (average !== null && Number.isFinite(average)) tri.average = average;
  return Object.keys(tri).length ? tri : null;
}

/** Prefer explicit server median/avg columns (distinct from nested `median` / `average`). */
function overlayMedianAverageOnTriplet(
  o: Record<string, unknown>,
  tri: PeerMeasureTriplet | null,
  medianKeys: string[],
  averageKeys: string[]
): PeerMeasureTriplet | null {
  const med = readDashboardNumber(o, medianKeys);
  const avg = readDashboardNumber(o, averageKeys);
  const merged: PeerMeasureTriplet = { ...(tri ?? {}) };
  if (med !== null && Number.isFinite(med)) merged.median = med;
  if (avg !== null && Number.isFinite(avg)) merged.average = avg;
  const hasAny =
    (merged.realValue != null && Number.isFinite(merged.realValue)) ||
    (merged.median != null && Number.isFinite(merged.median)) ||
    (merged.average != null && Number.isFinite(merged.average));
  if (!hasAny) return tri;
  return merged;
}

function readOptionalMetric(o: Record<string, unknown>, keys: string[]): number | undefined {
  const v = readDashboardNumber(o, keys);
  return v !== null && Number.isFinite(v) ? v : undefined;
}

/** Optional 1-based peer rank from the API (rounded). */
function readOptionalPeerRank(o: Record<string, unknown>, keys: string[]): number | undefined {
  const v = readDashboardNumber(o, keys);
  if (v === null || !Number.isFinite(v)) return undefined;
  const r = Math.round(v);
  return r >= 1 ? r : undefined;
}

function readTuitionBandRangeView(raw: unknown): SchoolComparisonTuitionBandRangeView | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const min = readDashboardNumber(o, ['minValue', 'MinValue', 'min', 'Min']);
  const max = readDashboardNumber(o, ['maxValue', 'MaxValue', 'max', 'Max']);
  const name = readDashboardString(o, ['name', 'Name'])?.trim();
  if (min === null || max === null || !Number.isFinite(min) || !Number.isFinite(max) || !(max > min)) return undefined;
  return { minValue: min, maxValue: max, name: name && name.length > 0 ? name : 'Band' };
}

/** Legacy API wording; peer group is same enrollment size + tuition band (school profile UI). */
function normalizePeerGroupCategoryLabel(label: string): string {
  return label === 'Same School & Tuition Band' ? 'Same School Size & Tuition Band' : label;
}

function mapPeerGroupNode(raw: unknown): SchoolComparisonPeerGroupView | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const rawLabel = readDashboardString(o, ['label', 'Label'])?.trim();
  if (!rawLabel) return null;
  const label = normalizePeerGroupCategoryLabel(rawLabel);
  const schoolCount = readDashboardNumber(o, ['schoolCount', 'SchoolCount']);
  const str = readDashboardNumber(o, ['avgStudentTeacherRatio', 'AvgStudentTeacherRatio']);
  const sal = readDashboardNumber(o, ['avgTeacherSalaryUSD', 'AvgTeacherSalaryUSD']);
  const cost = readDashboardNumber(o, ['avgCostPerStudentUSD', 'AvgCostPerStudentUSD']);
  const tuition = readDashboardNumber(o, [
    'avgTuitionPerStudentUSD',
    'AvgTuitionPerStudentUSD',
    'avgParentTuitionPerStudentUSD',
    'AvgParentTuitionPerStudentUSD',
    'avgParentTuitionUSD',
    'AvgParentTuitionUSD',
  ]);
  const tuitionTriplet = overlayMedianAverageOnTriplet(
    o,
    readMeasureTriplet(
      o,
      [
        'tuition',
        'Tuition',
        'tuitionMetric',
        'TuitionMetric',
        'tuitionPerStudent',
        'TuitionPerStudent',
        'tuitionPerStudentMetric',
        'TuitionPerStudentMetric',
      ],
      'tuition'
    ),
    ['medianTuitionPerStudentUSD', 'MedianTuitionPerStudentUSD'],
    ['avgTuitionPerStudentUSD', 'AvgTuitionPerStudentUSD']
  );
  const costTriplet = overlayMedianAverageOnTriplet(
    o,
    readMeasureTriplet(
      o,
      ['costPerStudent', 'CostPerStudent', 'costPerStudentMetric', 'CostPerStudentMetric'],
      'costPerStudent'
    ),
    ['medianCostPerStudentUSD', 'MedianCostPerStudentUSD'],
    ['avgCostPerStudentUSD', 'AvgCostPerStudentUSD']
  );
  const ratioTriplet = readMeasureTriplet(
    o,
    [
      'studentTeacherRatio',
      'StudentTeacherRatio',
      'studentTeacherRatioMetric',
      'StudentTeacherRatioMetric',
      'avgStudentTeacherRatioMetric',
      'AvgStudentTeacherRatioMetric',
    ],
    'studentTeacherRatio'
  );
  const salaryTriplet = readMeasureTriplet(
    o,
    [
      'teacherSalary',
      'TeacherSalary',
      'averageTeacherSalaryUSD',
      'AverageTeacherSalaryUSD',
      'avgTeacherSalaryUSDMetric',
      'AvgTeacherSalaryUSDMetric',
    ],
    'teacherSalary'
  );
  return {
    label,
    schoolCount: schoolCount === null || !Number.isFinite(schoolCount) ? 0 : Math.round(schoolCount),
    tuitionPerStudentUSD: readOptionalMetric(o, ['tuitionPerStudentUSD', 'TuitionPerStudentUSD']),
    medianTuitionPerStudentUSD:
      readOptionalMetric(o, ['medianTuitionPerStudentUSD', 'MedianTuitionPerStudentUSD']) ??
      tuitionTriplet?.median,
    minTuitionPerStudentUSD: readOptionalMetric(o, ['minTuitionPerStudentUSD', 'MinTuitionPerStudentUSD']),
    maxTuitionPerStudentUSD: readOptionalMetric(o, ['maxTuitionPerStudentUSD', 'MaxTuitionPerStudentUSD']),
    displayMinTuitionPerStudentUSD: readOptionalMetric(o, [
      'displayMinTuitionPerStudentUSD',
      'DisplayMinTuitionPerStudentUSD',
    ]),
    displayMaxTuitionPerStudentUSD: readOptionalMetric(o, [
      'displayMaxTuitionPerStudentUSD',
      'DisplayMaxTuitionPerStudentUSD',
    ]),
    p25TuitionPerStudentUSD: readOptionalMetric(o, ['p25TuitionPerStudentUSD', 'P25TuitionPerStudentUSD']),
    p75TuitionPerStudentUSD: readOptionalMetric(o, ['p75TuitionPerStudentUSD', 'P75TuitionPerStudentUSD']),
    costPerStudentUSD: readOptionalMetric(o, ['costPerStudentUSD', 'CostPerStudentUSD']),
    medianCostPerStudentUSD:
      readOptionalMetric(o, ['medianCostPerStudentUSD', 'MedianCostPerStudentUSD']) ?? costTriplet?.median,
    minCostPerStudentUSD: readOptionalMetric(o, ['minCostPerStudentUSD', 'MinCostPerStudentUSD']),
    maxCostPerStudentUSD: readOptionalMetric(o, ['maxCostPerStudentUSD', 'MaxCostPerStudentUSD']),
    displayMinCostPerStudentUSD: readOptionalMetric(o, [
      'displayMinCostPerStudentUSD',
      'DisplayMinCostPerStudentUSD',
    ]),
    displayMaxCostPerStudentUSD: readOptionalMetric(o, [
      'displayMaxCostPerStudentUSD',
      'DisplayMaxCostPerStudentUSD',
    ]),
    p25CostPerStudentUSD: readOptionalMetric(o, ['p25CostPerStudentUSD', 'P25CostPerStudentUSD']),
    p75CostPerStudentUSD: readOptionalMetric(o, ['p75CostPerStudentUSD', 'P75CostPerStudentUSD']),
    bandTuitionMin: readOptionalMetric(o, ['bandTuitionMin', 'BandTuitionMin']),
    bandTuitionMax: readOptionalMetric(o, ['bandTuitionMax', 'BandTuitionMax']),
    peerMinInBandTuition: readOptionalMetric(o, ['peerMinInBandTuition', 'PeerMinInBandTuition']),
    peerMaxInBandTuition: readOptionalMetric(o, ['peerMaxInBandTuition', 'PeerMaxInBandTuition']),
    closestBelowSchoolTuition: readOptionalMetric(o, [
      'closestBelowSchoolTuition',
      'ClosestBelowSchoolTuition',
    ]),
    closestAboveSchoolTuition: readOptionalMetric(o, [
      'closestAboveSchoolTuition',
      'ClosestAboveSchoolTuition',
    ]),
    budgetId: readDashboardString(o, ['budgetId', 'BudgetId']),
    minBudgetId: readDashboardString(o, ['minBudgetId', 'MinBudgetId']),
    maxBudgetId: readDashboardString(o, ['maxBudgetId', 'MaxBudgetId']),
    minBudgetSchoolName: readDashboardString(o, ['minBudgetSchoolName', 'MinBudgetSchoolName']),
    maxBudgetSchoolName: readDashboardString(o, ['maxBudgetSchoolName', 'MaxBudgetSchoolName']),
    costRank: readOptionalPeerRank(o, ['costRank', 'CostRank']),
    tuitionRank: readOptionalPeerRank(o, ['tuitionRank', 'TuitionRank']),
    tuition: tuitionTriplet,
    costPerStudent: costTriplet,
    studentTeacherRatio: ratioTriplet,
    teacherSalary: salaryTriplet,
    avgStudentTeacherRatio: str === null || !Number.isFinite(str) ? 0 : str,
    avgTeacherSalaryUSD: sal === null || !Number.isFinite(sal) ? 0 : sal,
    avgCostPerStudentUSD: cost === null || !Number.isFinite(cost) ? 0 : cost,
    avgTuitionPerStudentUSD: tuition === null || !Number.isFinite(tuition) ? 0 : tuition,
  };
}

/** Maps GET school-comparison/{schoolId} response (camelCase / PascalCase). */
export function mapSchoolClassificationSchoolComparison(
  raw: unknown
): SchoolClassificationSchoolComparisonView {
  if (!raw || typeof raw !== 'object') {
    return { peerGroupCategory1: null, peerGroupCategory2: null, peerGroupCategory3: null };
  }
  const root = raw as Record<string, unknown>;
  const targetRaw = readDashboardRaw(root, ['targetSchool', 'TargetSchool']);
  let dashboard: SchoolClassificationDashboardView | undefined;
  let targetTuitionPerStudentUSD: number | undefined;
  let targetCostPerStudentUSD: number | undefined;
  let targetTuitionBand: SchoolComparisonTuitionBandRangeView | undefined;
  let targetParentTuitionBand: SchoolComparisonTuitionBandRangeView | undefined;
  if (targetRaw && typeof targetRaw === 'object') {
    const t = targetRaw as Record<string, unknown>;
    dashboard = mapSchoolClassificationDashboard(t);
    const tu = readDashboardNumber(t, ['tuitionPerStudentUSD', 'TuitionPerStudentUSD']);
    const co = readDashboardNumber(t, ['costPerStudentUSD', 'CostPerStudentUSD']);
    if (tu !== null && Number.isFinite(tu)) targetTuitionPerStudentUSD = tu;
    if (co !== null && Number.isFinite(co)) targetCostPerStudentUSD = co;
    targetTuitionBand = readTuitionBandRangeView(readDashboardRaw(t, ['tuitionBand', 'TuitionBand']));
    targetParentTuitionBand = readTuitionBandRangeView(
      readDashboardRaw(t, ['parentTuitionBand', 'ParentTuitionBand', 'parentBand', 'ParentBand'])
    );
  }
  return {
    dashboard,
    targetTuitionPerStudentUSD,
    targetCostPerStudentUSD,
    targetTuitionBand,
    targetParentTuitionBand,
    peerGroupCategory1: mapPeerGroupNode(readDashboardRaw(root, ['peerGroupCategory1', 'PeerGroupCategory1'])),
    peerGroupCategory2: mapPeerGroupNode(readDashboardRaw(root, ['peerGroupCategory2', 'PeerGroupCategory2'])),
    peerGroupCategory3: mapPeerGroupNode(readDashboardRaw(root, ['peerGroupCategory3', 'PeerGroupCategory3'])),
  };
}

export const schoolClassificationApi = {
  /**
   * POST /api/app/school-classification/create-school-classification-based-on-bands
   */
  createBasedOnBands: async (body: CreateSchoolClassificationBasedOnBandsBody): Promise<void> => {
    await httpClient.post<unknown>(
      `${PREFIX}/create-school-classification-based-on-bands`,
      body
    );
  },

  /**
   * GET /api/app/school-classification/school-classificationfor-dashboard/{schoolId}?Year=
   */
  getSchoolClassificationForDashboard: async (
    schoolId: string,
    year: number
  ): Promise<SchoolClassificationDashboardView> => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}/school-classificationfor-dashboard/${encodeURIComponent(schoolId)}?Year=${encodeURIComponent(String(year))}`
    );
    return mapSchoolClassificationDashboard(raw);
  },

  /**
   * GET /api/app/school-classification/school-comparison/{schoolId}?year=
   */
  getSchoolComparison: async (
    schoolId: string,
    year: number
  ): Promise<SchoolClassificationSchoolComparisonView> => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}/school-comparison/${encodeURIComponent(schoolId)}?year=${encodeURIComponent(String(year))}`
    );
    return mapSchoolClassificationSchoolComparison(raw);
  },

  /**
   * GET /api/app/school-classification/class-tuition-by-program/{schoolId}?Year=
   * Tuition per student broken down by class program (روضة، اساسي، متوسط، ثانوي…).
   */
  getClassTuitionByProgram: async (
    schoolId: string,
    year: number
  ): Promise<ClassTuitionByProgramRow[]> => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}/class-tuition-by-program/${encodeURIComponent(schoolId)}?Year=${encodeURIComponent(String(year))}`
    );
    return mapClassTuitionByProgramRows(raw);
  },
};

function pickProgramTitle(o: Record<string, unknown>): string {
  return (
    readDashboardString(o, ['classProgramTitle', 'ClassProgramTitle', 'programTitle', 'ProgramTitle', 'name', 'Name']) ??
    ''
  );
}

export function mapClassTuitionByProgramRows(raw: unknown): ClassTuitionByProgramRow[] {
  if (!Array.isArray(raw)) return [];
  const rows: ClassTuitionByProgramRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const total = readDashboardNumber(o, ['totalTuition', 'TotalTuition']);
    const perStudent = readDashboardNumber(o, ['tuitionPerStudent', 'TuitionPerStudent']);
    const studentsRaw = readDashboardNumber(o, ['numberOfStudents', 'NumberOfStudents']);
    const students = studentsRaw === null ? 0 : Math.max(0, Math.round(studentsRaw));
    rows.push({
      classProgramTitle: pickProgramTitle(o),
      numberOfStudents: students,
      totalTuition: total === null || !Number.isFinite(total) ? 0 : total,
      tuitionPerStudent:
        perStudent === null || !Number.isFinite(perStudent) ? 0 : perStudent,
    });
  }
  return rows;
}
